import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
    type ChromaKeyPlayer,
    type ChromaKeySettings,
    DEFAULT_CHROMA_KEY_SETTINGS,
    type RendererMode,
    createChromaKeyPlayer,
    isChromaKeySupported,
} from '@/lib/chromaKey/chromaKeyRenderer'
import { cn } from '@/lib/utils'

export type ChromaKeyVideoHandle = {
    /** Adjust green-screen threshold at runtime (0–255) */
    setKeyThreshold: (threshold: number) => void
    getKeyThreshold: () => number
    setSettings: (settings: Partial<ChromaKeySettings>) => void
    getSettings: () => ChromaKeySettings
    getRendererMode: () => RendererMode
}

type ChromaKeyVideoProps = {
    src: string
    poster?: string
    className?: string
    /** Initial keying settings — threshold is the main knob to tune */
    settings?: Partial<ChromaKeySettings>
    preload?: 'auto' | 'metadata' | 'none'
    /** Start downloading only after the page has loaded and the main thread is idle (shows the poster meanwhile). */
    deferUntilIdle?: boolean
    onLoopComplete?: () => void
    'aria-label'?: string
}

/** Runs `task` once the window has loaded and the browser is idle; returns a cancel function. */
function scheduleAfterLoadWhenIdle(task: () => void): () => void {
    let cancelled = false
    let idleId: number | undefined
    let timeoutId: number | undefined

    const runWhenIdle = () => {
        if (cancelled) return
        if (typeof window.requestIdleCallback === 'function') {
            idleId = window.requestIdleCallback(() => !cancelled && task(), { timeout: 3000 })
        } else {
            timeoutId = window.setTimeout(() => !cancelled && task(), 1)
        }
    }

    if (document.readyState === 'complete') runWhenIdle()
    else window.addEventListener('load', runWhenIdle, { once: true })

    return () => {
        cancelled = true
        window.removeEventListener('load', runWhenIdle)
        if (idleId !== undefined) window.cancelIdleCallback?.(idleId)
        if (timeoutId !== undefined) window.clearTimeout(timeoutId)
    }
}

export const ChromaKeyVideo = forwardRef<ChromaKeyVideoHandle, ChromaKeyVideoProps>(function ChromaKeyVideo(
    {
        src,
        poster,
        className,
        settings,
        preload = 'auto',
        deferUntilIdle = false,
        onLoopComplete,
        'aria-label': ariaLabel,
    },
    ref,
) {
    const { t } = useTranslation()
    const containerRef = useRef<HTMLDivElement>(null)
    const videoRef = useRef<HTMLVideoElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const playerRef = useRef<ChromaKeyPlayer | null>(null)
    const onLoopCompleteRef = useRef(onLoopComplete)
    const [supported, setSupported] = useState(false)
    const [rendererMode, setRendererMode] = useState<RendererMode>('poster')
    const [aspectRatio, setAspectRatio] = useState('16 / 9')
    const [hasRenderedFrame, setHasRenderedFrame] = useState(false)

    useImperativeHandle(ref, () => ({
        setKeyThreshold: threshold => {
            playerRef.current?.setSettings({ threshold })
        },
        getKeyThreshold: () => playerRef.current?.getSettings().threshold ?? DEFAULT_CHROMA_KEY_SETTINGS.threshold,
        setSettings: partial => {
            playerRef.current?.setSettings(partial)
        },
        getSettings: () => playerRef.current?.getSettings() ?? { ...DEFAULT_CHROMA_KEY_SETTINGS, ...settings },
        getRendererMode: () => rendererMode,
    }))

    useEffect(() => {
        setSupported(isChromaKeySupported())
    }, [])

    useEffect(() => {
        onLoopCompleteRef.current = onLoopComplete
    }, [onLoopComplete])

    useEffect(() => {
        setHasRenderedFrame(false)
    }, [src])

    useEffect(() => {
        if (!supported) return

        const video = videoRef.current
        const canvas = canvasRef.current
        if (!video || !canvas) return

        let lastTime = 0
        let hasPlayed = false

        const onRatio = () => {
            if (video.videoWidth && video.videoHeight) {
                setAspectRatio(`${video.videoWidth} / ${video.videoHeight}`)
            }
        }

        video.addEventListener('loadedmetadata', onRatio)
        if (video.readyState >= 1) onRatio()

        const detectLoop = () => {
            if (!onLoopCompleteRef.current || !video.duration) return

            if (video.currentTime > 0.15) {
                hasPlayed = true
            }

            const looped = hasPlayed && lastTime > video.duration * 0.7 && video.currentTime < video.duration * 0.2

            if (looped) {
                hasPlayed = false
                onLoopCompleteRef.current()
            }

            lastTime = video.currentTime
        }

        video.addEventListener('timeupdate', detectLoop)

        const start = () => {
            video.preload = preload
            video.src = src
            video.load()

            const kickstartPlayback = () => {
                if (video.readyState >= 2) {
                    void video.play().catch(() => {})
                    return
                }

                if (video.preload !== 'auto') {
                    video.preload = 'auto'
                    video.load()
                }

                void video.play().catch(() => {})
            }

            if (preload !== 'auto') {
                video.addEventListener('loadedmetadata', kickstartPlayback, { once: true })
            }

            kickstartPlayback()

            playerRef.current = createChromaKeyPlayer({
                video,
                canvas,
                settings: { ...DEFAULT_CHROMA_KEY_SETTINGS, ...settings },
                onModeSelected: setRendererMode,
                onFirstFrame: () => setHasRenderedFrame(true),
            })
        }

        let cancelStart: (() => void) | undefined
        if (deferUntilIdle) cancelStart = scheduleAfterLoadWhenIdle(start)
        else start()

        return () => {
            cancelStart?.()
            video.removeEventListener('loadedmetadata', onRatio)
            video.removeEventListener('timeupdate', detectLoop)
            playerRef.current?.destroy()
            playerRef.current = null
        }
        // settings intentionally applied only on mount; use ref API for live tuning
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supported, src, preload, deferUntilIdle])

    if (!supported) {
        return (
            <div ref={containerRef} className={className} style={{ aspectRatio }} role='img' aria-label={ariaLabel}>
                {poster ? (
                    <img src={poster} alt='' className='h-full w-full object-contain' />
                ) : (
                    <div className='flex h-full w-full items-center justify-center bg-transparent text-sm text-[#B8AFA3]'>
                        {t('foodCost.chromaKey.animationUnavailable', 'Animación no disponible')}
                    </div>
                )}
            </div>
        )
    }

    return (
        <div
            ref={containerRef}
            className={cn('relative', className)}
            style={{ aspectRatio }}
            role='img'
            aria-label={ariaLabel}
        >
            {poster && !hasRenderedFrame ? (
                <img src={poster} alt='' aria-hidden className='absolute inset-0 h-full w-full object-contain' />
            ) : null}
            <video
                ref={videoRef}
                autoPlay
                muted
                loop
                playsInline
                preload={preload}
                className='pointer-events-none absolute h-0 w-0 opacity-0'
                aria-hidden
            />
            <canvas ref={canvasRef} className='relative block h-full w-full bg-transparent object-contain' />
        </div>
    )
})
